import type { InteractionEvent, DragHandle } from '../..';
import type { IViewport } from '../..';
import { DistanceMeasurementOverlay } from '../../display/overlay-actors/distance-measurement-overlay';
import type { Vec3 } from '../../domain';
import type { ITool } from '../types';

/**
 * State machine for the distance measurement tool.
 *
 * Transitions:
 *   idle → (activate) → placing_first
 *   placing_first → (click) → placing_second
 *   placing_second → (click) → completed
 *   any → (cancel/Escape) → completed (with cleanup)
 */
type ToolPhase =
    | { phase: 'none' }
    | { phase: 'placing_first' }
    | {
          phase: 'placing_second';
          point1: Vec3;
      }
    | { phase: 'idle' };

export class DistanceMeasurementTool implements ITool {
    static readonly toolName = 'distance-measurement';

    readonly id = DistanceMeasurementTool.toolName;
    readonly name = 'Distance';
    readonly cursor = 'crosshair';
    overlay: DistanceMeasurementOverlay | null = null;
    completed = false;

    private state: ToolPhase = { phase: 'none' };
    private viewport: IViewport | null = null;

    hoverable = true;

    private dragState: {
        handle: 'point1' | 'point2';
        originalWorld: Vec3;
        overlay: DistanceMeasurementOverlay;
    } | null = null;

    // ═══════════════════════════════════════════
    //  ITool lifecycle
    // ═══════════════════════════════════════════

    activate(viewport: IViewport): void {
        this.viewport = viewport;
        this.state = { phase: 'placing_first' };
        this.completed = false;
    }

    deactivate(): void {
        this.cleanupPreview();
        this.state = { phase: 'none' };
    }

    cancel(): void {
        this.cleanupPreview();
        this.state = { phase: 'none' };
        this.completed = true;
    }

    // ═══════════════════════════════════════════
    //  Event handling — delegates to phase handler
    // ═══════════════════════════════════════════

    handleEvent(event: InteractionEvent): boolean {
        if (!this.viewport) return false;

        switch (this.state.phase) {
            case 'placing_first':
                return this.handlePlacingFirst(event);
            case 'placing_second':
                return this.handlePlacingSecond(event);
            case 'idle':
                return this.handleIdle(event);
            default:
                return false;
        }
    }

    // ═══════════════════════════════════════════
    //  Phase: placing_first
    //  Waiting for the user to click the first point.
    //  pointer_move is NOT consumed → allows 3D hover.
    // ═══════════════════════════════════════════

    private handlePlacingFirst(event: InteractionEvent): boolean {
        // Only react to left-click
        if (!this.viewport) return false;

        if (event.type !== 'pointer_down' || event.button !== 0) {
            return false;
        }

        const worldPos = event.currentPoints.world;
        if (!worldPos) return false;

        // Create the preview overlay with both points at the click position
        const preview = new DistanceMeasurementOverlay(worldPos, worldPos, this.viewport);
        this.viewport!.getOverlayLayer().addOverlayObject(preview);

        this.state = {
            phase: 'placing_second',
            point1: worldPos,
        };

        this.overlay = preview;

        return true; // Consumed: don't do camera pan
    }

    // ═══════════════════════════════════════════
    //  Phase: placing_second
    //  First point is placed. Mouse move updates preview.
    //  Click finalizes the measurement.
    // ═══════════════════════════════════════════

    private handlePlacingSecond(event: InteractionEvent): boolean {
        if (!this.viewport) return false;

        if (this.state.phase !== 'placing_second' || !this.overlay) return false;

        // ── Mouse move: update preview endpoint ──
        if (event.type === 'mouse_drag') {
            const worldPos = event.currentPoints.world;
            if (worldPos) {
                this.overlay.setPoint2(worldPos);
                this.viewport!.getOverlayLayer().render();
            }
            return true; // Consumed: no 3D hover during placement
        }

        // ── Left click: finalize measurement ──
        if (event.type === 'mouse_drag_end' && event.button === 0) {
            const worldPos = event.currentPoints.world;
            if (!worldPos) return false;

            // Prevent creating a measurement when deltaPoints is null or unchanged
            const start = event.startPoints?.world;
            const current = event.currentPoints?.world;
            if (
                !start ||
                !current ||
                (start[0] === current[0] && start[1] === current[1] && start[2] === current[2])
            ) {
                this.cancel();
                return true;
            }

            // Set final position and make interactive
            this.overlay.setPoint2(worldPos);
            this.overlay.finalize();

            // Render to show the finalized state
            this.viewport!.getOverlayLayer().render();

            // Transition to idle → InteractionService will set us to passive and restore the previous tool
            this.state = { phase: 'idle' };
            this.completed = true;
            return true;
        }

        // ── Right click: cancel ──
        if (event.type === 'key_down' && event.key === 'Escape') {
            this.cancel();
            return true;
        }

        return false;
    }

    private handleIdle(event: InteractionEvent): boolean {
        if (!this.viewport) return false;
        if (this.state.phase !== 'idle' || !this.overlay) return false;

        // ── Pointer move: detect hover over drag handles ──
        if (event.type === 'pointer_move') {
            const handle = this.findHandleAtPosition(
                event.currentPoints.canvas[0],
                event.currentPoints.canvas[1]
            );
            if (handle) {
                this.overlay.setIsHovered(true);
            } else if (!this.dragState) {
                this.overlay.setIsHovered(false);
            }
            this.viewport.getOverlayLayer().render();
            return false; // Don't consume — allow camera/other tools to handle
        }

        // ── Left click on drag handle: start dragging ──
        if (event.type === 'pointer_down' && event.button === 0) {
            const handle = this.findHandleAtPosition(
                event.currentPoints.canvas[0],
                event.currentPoints.canvas[1]
            );
            if (!handle) return false;

            const handleId = handle.id as 'point1' | 'point2';
            const original =
                handleId === 'point1' ? this.overlay.getPoint1World() : this.overlay.getPoint2World();

            this.dragState = { handle: handleId, originalWorld: original, overlay: this.overlay };
            return true;
        }

        // ── Mouse drag: move the point to the drag position ──
        if (event.type === 'mouse_drag') {
            if (!this.dragState) return false;

            const worldPos = event.currentPoints.world;
            if (!worldPos) return false;

            if (this.dragState.handle === 'point1') {
                this.dragState.overlay.setPoint1(worldPos);
            } else {
                this.dragState.overlay.setPoint2(worldPos);
            }
            this.viewport.getOverlayLayer().render();
            return true;
        }

        // ── Mouse drag end: confirm the new position ──
        if (event.type === 'mouse_drag_end') {
            if (!this.dragState) return false;

            const worldPos = event.currentPoints.world;
            if (worldPos) {
                if (this.dragState.handle === 'point1') {
                    this.dragState.overlay.setPoint1(worldPos);
                } else {
                    this.dragState.overlay.setPoint2(worldPos);
                }
            }
            this.dragState = null;
            this.viewport.getOverlayLayer().render();
            return true;
        }

        // ── Escape during drag: restore original position ──
        if (event.type === 'key_down' && event.key === 'Escape') {
            if (!this.dragState) return false;

            if (this.dragState.handle === 'point1') {
                this.dragState.overlay.setPoint1(this.dragState.originalWorld);
            } else {
                this.dragState.overlay.setPoint2(this.dragState.originalWorld);
            }
            this.dragState = null;
            this.viewport.getOverlayLayer().render();
            return true;
        }

        return false;
    }

    getDragHandles(): DragHandle[] {
        if (this.state.phase !== 'idle' || !this.overlay || !this.overlay.interactive) return [];
        const overlay = this.overlay;
        return [
            {
                id: 'point1',
                position: overlay.getPoint1Canvas(),
                cursor: 'grab',
                hitRadius: overlay.getHandleHitRadius(),
            },
            {
                id: 'point2',
                position: overlay.getPoint2Canvas(),
                cursor: 'grab',
                hitRadius: overlay.getHandleHitRadius(),
            },
        ];
    }

    // ═══════════════════════════════════════════
    //  Hit testing
    // ═══════════════════════════════════════════

    private findHandleAtPosition(canvasX: number, canvasY: number): DragHandle | null {
        const handles = this.getDragHandles();
        for (const handle of handles) {
            const dx = canvasX - handle.position.x;
            const dy = canvasY - handle.position.y;
            if (dx * dx + dy * dy <= handle.hitRadius * handle.hitRadius) {
                return handle;
            }
        }
        return null;
    }

    // ═══════════════════════════════════════════
    //  Cleanup
    // ═══════════════════════════════════════════

    /**
     * Remove the preview overlay if we're in the middle of placing.
     * After finalize(), the overlay stays (it's now a permanent measurement).
     */
    private cleanupPreview(): void {
        if (this.state.phase === 'placing_second' && this.overlay && !this.overlay.interactive) {
            // Only remove if not yet finalized
            this.viewport?.getOverlayLayer().removeOverlayObject(this.overlay.id);
            this.viewport?.getOverlayLayer().render();
        }
    }
}
